import createDOMPurify from 'isomorphic-dompurify';

// Maintain backward compatibility with IFilterXSSOptions interface

// Convert xss whiteList format to DOMPurify config
const convertWhiteListToDOMPurifyConfig = whiteList => {
  const allowedTags = Object.keys(whiteList);
  const allowedAttrs = new Set();

  // Collect all unique attributes from the whiteList
  Object.values(whiteList).forEach(attrs => {
    if (Array.isArray(attrs)) {
      attrs.forEach(attr => allowedAttrs.add(attr));
    }
  });
  return {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: Array.from(allowedAttrs)
  };
};
export const sanitizeHtml = function (html, xssOptions) {
  if (xssOptions === void 0) {
    xssOptions = {};
  }
  // Build the whiteList similar to original implementation:
  // 1. Start with xss default whitelist (DOMPurify's defaults are similar, so we use DOMPurify's defaults)
  // 2. Add custom tags/attributes: span, div, a, source, kbd, ol
  // 3. Merge with xssOptions (which can override)

  const defaultWhiteList = {
    // Common HTML tags with their typical attributes (matching xss defaults)
    a: ['target', 'href', 'title', 'data-hook', 'data-attachment'],
    abbr: ['title'],
    address: [],
    area: ['shape', 'coords', 'href', 'alt'],
    article: [],
    aside: [],
    audio: ['autoplay', 'controls', 'loop', 'muted', 'preload', 'src'],
    b: [],
    bdi: ['dir'],
    bdo: ['dir'],
    big: [],
    blockquote: ['cite'],
    body: [],
    br: [],
    button: ['autofocus', 'disabled', 'form', 'name', 'type', 'value'],
    canvas: ['width', 'height'],
    caption: [],
    center: [],
    cite: [],
    code: [],
    col: ['align', 'valign', 'span', 'width'],
    colgroup: ['align', 'valign', 'span', 'width'],
    dd: [],
    del: ['datetime'],
    details: ['open'],
    dfn: [],
    dialog: ['open'],
    div: ['style'],
    dl: [],
    dt: [],
    em: [],
    embed: ['src', 'type', 'width', 'height'],
    fieldset: ['disabled', 'form', 'name'],
    figcaption: [],
    figure: [],
    font: ['color', 'size', 'face'],
    footer: [],
    form: ['name', 'action', 'method', 'enctype', 'autocomplete', 'novalidate', 'target'],
    h1: [],
    h2: [],
    h3: [],
    h4: [],
    h5: [],
    h6: [],
    head: [],
    header: [],
    hgroup: [],
    hr: [],
    html: ['manifest'],
    i: [],
    img: ['src', 'alt', 'title', 'width', 'height'],
    input: ['type', 'name', 'value', 'disabled', 'checked', 'autofocus', 'form', 'required'],
    ins: ['datetime'],
    kbd: [],
    label: ['form', 'for'],
    legend: [],
    li: ['value'],
    link: ['href', 'rel', 'media', 'hreflang', 'type', 'sizes'],
    main: [],
    map: ['name'],
    mark: [],
    nav: [],
    ol: ['class'],
    optgroup: ['disabled', 'label'],
    option: ['disabled', 'selected', 'value'],
    p: [],
    param: ['name', 'value'],
    pre: [],
    q: ['cite'],
    s: [],
    samp: [],
    section: [],
    small: [],
    source: ['src', 'type'],
    span: ['style'],
    strong: [],
    style: ['type', 'media', 'scoped'],
    sub: [],
    summary: [],
    sup: [],
    table: ['width', 'border', 'align', 'valign'],
    tbody: ['align', 'valign'],
    td: ['width', 'colspan', 'align', 'valign', 'rowspan'],
    tfoot: ['align', 'valign'],
    th: ['width', 'colspan', 'align', 'valign', 'rowspan'],
    thead: ['align', 'valign'],
    title: [],
    tr: ['rowspan', 'align', 'valign'],
    u: [],
    ul: [],
    video: ['autoplay', 'controls', 'loop', 'muted', 'preload', 'src', 'height', 'width']
  };

  // Merge with custom additions (matching original code)
  const mergedWhiteList = {
    ...defaultWhiteList,
    span: ['style'],
    // in the BO rich text component transform all changes to span with style
    div: ['style'],
    // allow to override common styles for components like Agent Tooltip content
    a: ['target', 'href', 'title', 'data-hook', 'data-attachment'],
    // added data-hook for testing; data-attachment for file attachment links
    source: ['src', 'type'],
    // correct playing video in attachments
    kbd: [],
    // multiline input field keyboard tips
    ol: ['class'],
    ...xssOptions.whiteList // Merge with user-provided options (can override)
  };

  // Convert to DOMPurify config
  const config = convertWhiteListToDOMPurifyConfig(mergedWhiteList);

  // Handle other xss options
  if (xssOptions.stripIgnoreTag) {
    config.ALLOWED_TAGS = [];
  }
  if (xssOptions.stripIgnoreTagBody && Array.isArray(xssOptions.stripIgnoreTagBody)) {
    config.FORBID_TAGS = xssOptions.stripIgnoreTagBody;
  }
  return createDOMPurify.sanitize(html, config);
};
export const escapeHtml = value => {
  // Use DOMPurify to escape HTML by sanitizing with no allowed tags but keeping content
  return createDOMPurify.sanitize(value, {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true
  });
};
export const sanitizeInput = input => {
  // Strip script tags and other dangerous content
  return createDOMPurify.sanitize(input, {
    FORBID_TAGS: ['script'],
    FORBID_ATTR: ['onerror', 'onload']
  });
};
//# sourceMappingURL=xss.js.map